import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import BaseTypeSection from './BaseTypeSection';
import './MaintenanceTemplatesEditor.css';
import {ComponentLifetime, TemplatesByType} from "./types";
import {BaseType, BuildComponentType, getComponentsByBaseType} from "../../../types/Enums";
import { RELEVANT_BASE_TYPES } from '../../../Helpers/baseTypeUtils';
import { Helmet } from 'react-helmet-async';

const MaintenanceTemplatesEditor: React.FC = () => {
    const [templates, setTemplates] = useState<TemplatesByType>({});
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [expandedBaseSections, setExpandedBaseSections] = useState<Set<string>>(new Set());
    const [expandedComponentSections, setExpandedComponentSections] = useState<Set<string>>(new Set());
    const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
    const [editingLifetime, setEditingLifetime] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    const db = getDatabase();

    // Load templates from Firebase
    const loadTemplates = async (): Promise<void> => {
        try {
            setLoading(true);
            const templatesRef = ref(db, 'maintenance/templates');
            const snapshot = await get(templatesRef);

            const templatesByType: TemplatesByType = {};
            // Initialize all component types with empty arrays
            Object.values(BuildComponentType).forEach((type: BuildComponentType) => {
                templatesByType[type] = [];
            });

            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, Record<string, any>>;
                Object.entries(data).forEach(([componentType, componentTemplates]) => {
                    if (componentTemplates && typeof componentTemplates === 'object') {
                        templatesByType[componentType] = Object.entries(componentTemplates).map(([id, template]: [string, any]) => ({
                            id,
                            name: template.name || '',
                            description: template.description || '',
                            interval_type: template.interval_type || 'years',
                            interval_value: template.interval_value || 1,
                            // Back-compat: map old cost_per_sqm → cost + cost_unit
                            cost:
                                typeof template.cost === 'number'
                                    ? template.cost
                                    : typeof template.cost_per_sqm === 'number'
                                        ? template.cost_per_sqm
                                        : 0,
                            cost_unit:
                                template.cost_unit === 'per_unit' || template.cost_unit === 'per_sqm'
                                    ? template.cost_unit
                                    : typeof template.cost_per_sqm === 'number'
                                        ? 'per_sqm'
                                        : 'per_sqm',
                            // NEW: Load directional intervals
                            directionalIntervals: template.directionalIntervals || undefined
                        }));
                    }
                });
            }

            setTemplates(templatesByType);
        } catch (error) {
            console.error('Error loading templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadComponentLifetimes = async (): Promise<void> => {
        try {
            const lifetimesRef = ref(db, 'maintenance/componentLifetimes');
            const snapshot = await get(lifetimesRef);
            if (snapshot.exists()) {
                const data = snapshot.val() as Record<string, any>;

                // Process the data to ensure backward compatibility
                const processedLifetimes: Record<string, ComponentLifetime> = {};

                Object.entries(data).forEach(([componentType, lifetime]: [string, any]) => {
                    processedLifetimes[componentType] = {
                        expectedLifetimeYears: lifetime.expectedLifetimeYears || 0,
                        replacementCost: lifetime.replacementCost,
                        costUnit: lifetime.costUnit || 'per_sqm',
                        directionalLifetimes: lifetime.directionalLifetimes || undefined
                    };
                });

                setComponentLifetimes(processedLifetimes);
            }
        } catch (error) {
            console.error('Error loading component lifetimes:', error);
        }
    };

    useEffect(() => {
        loadTemplates().then(r => {});
        loadComponentLifetimes().then(r => {});
    }, []);

    const toggleBaseSection = (baseType: string): void => {
        const newExpanded = new Set(expandedBaseSections);
        if (newExpanded.has(baseType)) newExpanded.delete(baseType);
        else newExpanded.add(baseType);
        setExpandedBaseSections(newExpanded);
    };

    const toggleComponentSection = (componentType: string): void => {
        const newExpanded = new Set(expandedComponentSections);
        if (newExpanded.has(componentType)) newExpanded.delete(componentType);
        else newExpanded.add(componentType);
        setExpandedComponentSections(newExpanded);
    };

    const getBaseTypeTemplateCount = (baseType: BaseType): number => {
        const componentTypes = getComponentsByBaseType(baseType);
        return componentTypes.reduce((total, componentType) => total + (templates[componentType]?.length || 0), 0);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted">Loading templates...</h5>
                </div>
            </div>
        );
    }

    const relevantBaseTypes = RELEVANT_BASE_TYPES;

    return (
        <div className="maintenance-templates-editor" >
            <Helmet>
                <title>Underhållsmallar - Molnfastigheter</title>
            </Helmet>
            {/* Header */}
                <div className="header-content" style={{marginBottom: '30px'}} >
                    <h1 className="display-6 fw-bold text-dark mb-2">Underhållsmallar</h1>
                    <p className="lead text-muted mb-0">Hantera mallar för återkommande underhåll och beräknad livslängd</p>
                </div>

            {/* Main Content */}
            <div className="main-content-templates-editor" style={{marginLeft: '0px'}}>
                <div className="accordion" id="maintenanceAccordion">
                    {Object.values(relevantBaseTypes).map((baseType: BaseType) => (
                        <BaseTypeSection
                            key={baseType}
                            baseType={baseType}
                            templates={templates}
                            setTemplates={setTemplates}
                            componentLifetimes={componentLifetimes}
                            setComponentLifetimes={setComponentLifetimes}
                            expandedBaseSections={expandedBaseSections}
                            expandedComponentSections={expandedComponentSections}
                            editingTemplate={editingTemplate}
                            editingLifetime={editingLifetime}
                            saving={saving}
                            toggleBaseSection={toggleBaseSection}
                            toggleComponentSection={toggleComponentSection}
                            setEditingTemplate={setEditingTemplate}
                            setEditingLifetime={setEditingLifetime}
                            setSaving={setSaving}
                            getBaseTypeTemplateCount={getBaseTypeTemplateCount}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MaintenanceTemplatesEditor;